import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import Box from "@mui/material/Box";
import HorizontalSteps from "./horizontal-stepper/HorizontalSteps";
import VerticalSteps from "./vertical-stepper/VerticalStepper";
import {IconButton, useMediaQuery} from "@mui/material";
import {ExpandCircleDownOutlined} from "@mui/icons-material";
import {RoutePaths} from "../../../util/constants";
import {RootState, useAppSelector} from "../../../store";
import useHowItWorksJsonLd from "./useHowItWorksJsonLd";
import React, {useMemo, useState} from "react";
import {Link as RouterLink} from "react-router-dom";
import WishTypePicker from "../../shared/wish-type-picker/WishTypePicker";
import {WishType} from "../../../store/wish/Helper";

const HowItWorks = () => {
    const {t, i18n} = useTranslation()
    const matches = useMediaQuery('(min-width:725px)');
    const homeContent = useAppSelector((state: RootState) => state.homeReducer.content)
    const homeSteps = homeContent.how_it_works?.steps
    const [type, setType] = useState<WishType>(WishType.DRAW)
    const selectedSteps = useMemo(() => homeSteps?.[type] ?? [], [homeSteps, type])
    const {content} = useHowItWorksJsonLd(selectedSteps)
    const howItWorksSubtitle = useMemo(() => {
        const locale = i18n.language?.toLowerCase() ?? "en";
        const subtitle = locale.startsWith("mk")
            ? homeContent?.how_it_works?.subtitle_mk
            : locale.startsWith("al")
                ? homeContent?.how_it_works?.subtitle_al
                : homeContent?.how_it_works?.subtitle_en;
        if (typeof subtitle === "string" && subtitle.trim()) {
            return subtitle.trim();
        }
        return t("home_how_it_works_subtitle");
    }, [homeContent, i18n.language, t])

    return (
        <Box mt={12}>
            <script
                type="application/ld+json">
                {JSON.stringify(content)}
            </script>
            <Box
                display="flex"
                alignItems="center"
                columnGap={1}
            >
                <Typography
                    variant="h4"
                    color="primary"
                    fontWeight={600}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        whiteSpace: "nowrap",
                    }}
                >
                    {t('how_it_works')}
                    <IconButton
                        color="primary"
                        size="small"
                        component={RouterLink}
                        to={RoutePaths.HOW_IT_WORKS}
                        aria-label={t('view_how_it_works')}
                        sx={{ml: 1}}
                    >
                        <ExpandCircleDownOutlined sx={{transform: 'rotate(-90deg)'}}/>
                    </IconButton>
                </Typography>
            </Box>
            <Typography
                variant="body1"
                color="primary"
                sx={{mt: 0.75, opacity: 0.9}}
            >
                {howItWorksSubtitle}
            </Typography>
            <WishTypePicker
                color="primary"
                plural
                value={type}
                handleChange={(value) => setType(value ?? WishType.DRAW)}
                sx={{mt: 1.5}}
            />

            {matches ?
                <HorizontalSteps steps={selectedSteps}/> :
                <VerticalSteps steps={selectedSteps}/>}
        </Box>
    )
}

export default HowItWorks
